import React, { useState, useEffect, useRef, useCallback } from 'react';
import { collection, addDoc, query, orderBy, onSnapshot, deleteDoc, doc, getDoc, limit, startAfter, getDocs } from 'firebase/firestore';
import { useFireAuth } from '../FireAuthContext';
import { db } from '../App';
import './ChatRoom.css';
import { FaPaperPlane, FaTrash } from 'react-icons/fa';
import MembershipTrophy from './MembershipTrophy';

const ChatRoom = () => {
  const { user } = useFireAuth();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [lastVisible, setLastVisible] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [userDetails, setUserDetails] = useState({});
  const messagesEndRef = useRef(null);
  const messagesStartRef = useRef(null);
  const sendingRef = useRef(false);

  const fetchUserDetails = useCallback(async (userId) => {
    if (!userDetails[userId]) {
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (userDoc.exists()) {
        setUserDetails((prevDetails) => ({ ...prevDetails, [userId]: userDoc.data() }));
      }
    }
  }, [userDetails]);

  const loadInitialMessages = useCallback(() => {
    const q = query(collection(db, 'chatRoom'), orderBy('timestamp', 'desc'), limit(10));
    const unsubscribe = onSnapshot(q, async (snapshot) => {
      const messagesArray = await Promise.all(
        snapshot.docs.map(async (doc) => {
          const data = doc.data();
          await fetchUserDetails(data.userId);
          return { id: doc.id, ...data };
        })
      );
      setMessages(messagesArray.reverse());
      setLastVisible(snapshot.docs[snapshot.docs.length - 1]);
    });

    return unsubscribe;
  }, [fetchUserDetails]);

  const loadMoreMessages = useCallback(async () => {
    if (loadingMore || !lastVisible) return;

    setLoadingMore(true);
    const q = query(collection(db, 'chatRoom'), orderBy('timestamp', 'desc'), startAfter(lastVisible), limit(10));
    const snapshot = await getDocs(q);

    if (snapshot.docs.length > 0) {
      const moreMessages = await Promise.all(
        snapshot.docs.map(async (doc) => {
          const data = doc.data();
          await fetchUserDetails(data.userId);
          return { id: doc.id, ...data };
        })
      );
      moreMessages.reverse();
      setMessages((prevMessages) => [...moreMessages, ...prevMessages]);
      setLastVisible(snapshot.docs[snapshot.docs.length - 1]);
    }

    setLoadingMore(false);
  }, [fetchUserDetails, lastVisible, loadingMore]);

  const checkAdminStatus = useCallback(async () => {
    const adminDoc = await getDoc(doc(db, 'admins', user.uid));
    if (adminDoc.exists()) {
      setIsAdmin(true);
    }
  }, [user.uid]);

  useEffect(() => {
    if (user) {
      const unsubscribe = loadInitialMessages();
      fetchUserDetails(user.uid);
      checkAdminStatus();
      return unsubscribe;
    }
  }, [user, loadInitialMessages, fetchUserDetails, checkAdminStatus]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (newMessage.trim() === '' || sendingRef.current) return;

    sendingRef.current = true;
    try {
      await addDoc(collection(db, 'chatRoom'), {
        userId: user.uid,
        content: newMessage,
        timestamp: new Date(),
      });
      setNewMessage('');
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      sendingRef.current = false;
    }
  };

  const handleDeleteMessage = async (messageId, messageUserId) => {
    if (isAdmin || user.uid === messageUserId) {
      try {
        await deleteDoc(doc(db, 'chatRoom', messageId));
      } catch (error) {
        console.error('Error deleting message:', error);
      }
    }
  };

  return (
    <div className="chat-room">
      <div
        className="chat-messages"
        ref={messagesStartRef}
        onScroll={(e) => {
          if (e.target.scrollTop === 0) {
            loadMoreMessages();
          }
        }}
      >
        {messages.map((message) => {
          const userDetail = userDetails[message.userId];
          return (
            <div key={message.id} className="chat-message">
              <div className="chat-message-header">
                <img
                  src={userDetail?.profilePicture}
                  alt={`${userDetail?.name}'s profile`}
                  className="chat-message-pic"
                />
                <MembershipTrophy status={userDetail?.membershipStatus} />
                <span className="chat-message-name">{userDetail?.name}</span>
                <span className="chat-message-membership">#{userDetail?.membershipNumber}</span>
                {(isAdmin || user.uid === message.userId) && (
                  <FaTrash
                    className="delete-message-icon"
                    onClick={() => handleDeleteMessage(message.id, message.userId)}
                  />
                )}
              </div>
              <div className="chat-message-content">{message.content}</div>
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>
      <form onSubmit={handleSendMessage} className="chat-input-form">
        <input
          type="text"
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          placeholder="Type your message..."
          className="chat-input"
        />
        <button type="submit" className="chat-send-button">
          <FaPaperPlane />
        </button>
      </form>
    </div>
  );
};

export default ChatRoom;
